/** A monitor request still pending after this long is cancelled. */
export const MONITOR_REQUEST_TIMEOUT_MS = 20 * 1000;
/**
 * The spotlight's trip, detail and alerts refresh this often while the same
 * shipment stays in focus (a paused screen). A 10 s spotlight never gets
 * there, so the rotation itself adds no requests.
 */
export const FOCUS_REFRESH_MS = 60 * 1000;

/**
 * `next`, unless it has the same content as `current`: a refresh that
 * changed nothing keeps the old object, so nothing downstream redraws or
 * reframes the map.
 */
export const keepIfSame = <T>(current: T, next: T): T =>
  JSON.stringify(current) === JSON.stringify(next) ? current : next;

/**
 * Polls `load` one request at a time, for a screen that runs for days. The
 * next request starts `intervalMs` after the previous one settles, so a slow
 * endpoint never piles up requests or lets an older answer land after a
 * newer one; a request still pending after `timeoutMs` is aborted. The
 * returned function stops the loop and aborts the request in flight.
 *
 * An abort ends the run even if `load` ignores its signal (a client that
 * takes none), so a hung request never stalls the loop.
 *
 * `load` handles its own result and errors; it should ignore both once its
 * signal is aborted.
 */
export const startPolling = (
  load: (signal: AbortSignal) => Promise<unknown>,
  {
    intervalMs,
    timeoutMs = MONITOR_REQUEST_TIMEOUT_MS,
  }: { intervalMs: number; timeoutMs?: number }
): (() => void) => {
  let stopped = false;
  let controller: AbortController | null = null;
  let nextRun: number | undefined;

  const run = () => {
    if (stopped) return;
    const current = new AbortController();
    controller = current;
    const timeout = window.setTimeout(() => current.abort(), timeoutMs);
    const aborted = new Promise<void>((resolve) => {
      current.signal.addEventListener('abort', () => resolve(), {
        once: true,
      });
    });
    Promise.resolve()
      .then(() => Promise.race([load(current.signal), aborted]))
      .catch((): void => undefined)
      .finally(() => {
        window.clearTimeout(timeout);
        if (!stopped) nextRun = window.setTimeout(run, intervalMs);
      });
  };

  run();
  return () => {
    stopped = true;
    window.clearTimeout(nextRun);
    controller?.abort();
  };
};
